import { useEffect, useCallback } from 'react'
import { useUser, useAuth } from '@clerk/clerk-react'
import { useUserStore } from '@/stores/user.store'
import { getCurrentUser } from '@/services/auth.service'

/**
 * Syncs Clerk authentication with backend user profile.
 * - Updates store with Clerk user data
 * - Fetches backend profile after Clerk auth is confirmed
 * - Handles loading and error states
 *
 * Returns { isReady, isLoading, error } for UI feedback
 */
export function useAuthSync() {
  const { user: clerkUser, isSignedIn, isLoaded: isClerkLoaded } = useUser()
  const { isLoaded: isAuthLoaded } = useAuth()

  const {
    profile,
    isProfileLoading,
    profileError,
    setClerkUser,
    setProfile,
    setProfileLoading,
    setProfileError,
    clearUser,
  } = useUserStore()

  const fetchBackendProfile = useCallback(async () => {
    setProfileLoading(true)
    try {
      const userProfile = await getCurrentUser()
      setProfile(userProfile)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch user profile'
      setProfileError(message)
      console.error('Failed to fetch backend user profile:', error)
    }
  }, [setProfile, setProfileLoading, setProfileError])

  useEffect(() => {
    // Wait for Clerk to be fully loaded
    if (!isClerkLoaded || !isAuthLoaded) {
      return
    }

    if (isSignedIn && clerkUser) {
      // Update store with Clerk user data
      setClerkUser({
        id: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        firstName: clerkUser.firstName || '',
        lastName: clerkUser.lastName || '',
        imageUrl: clerkUser.imageUrl,
      })

      // Fetch backend profile if we don't have it yet
      if (!profile && !isProfileLoading) {
        fetchBackendProfile()
      }
    } else {
      // User signed out - clear all data
      clearUser()
    }
  }, [
    isClerkLoaded,
    isAuthLoaded,
    isSignedIn,
    clerkUser,
    profile,
    isProfileLoading,
    setClerkUser,
    clearUser,
    fetchBackendProfile,
  ])

  return {
    isReady: isClerkLoaded && isAuthLoaded && !!profile && !isProfileLoading,
    isLoading: !isClerkLoaded || !isAuthLoaded || isProfileLoading,
    error: profileError,
    refetch: fetchBackendProfile,
  }
}
